import { useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Box, HStack, useTheme, View, VStack } from 'native-base';
import { flatMap, map, orderBy, startCase, toLower } from 'lodash';

import { ButtonGroup, FaceSmileIcon, Text } from '@pimm/base';
import { EventThresholdMode, EventTypeDto } from '@pimm/services/lib/threshold-profiles';
import { convertMinutesToHoursAndMinutes, dateDiffToMinutes, formatToShortDateTime, stringToDateLocal } from '@app/utils/date-formatter';
import VirtualTable from '@app/components/virtual-table';
import { VirtualColumnProps } from '@app/components/virtual-table/virtual-table';
import { ResourceLoader } from '@app/components/shared';
import { useEquipmentConfig } from '../context/equipment-config.context';
import { useGetEventCounts, useGetStoreEvents } from '../hooks';
import { BatteryLowIcon, DoorEventIcon, Thermometer02Icon, WifiOffIcon } from '../icons';
import { getViolationTriggerLabel } from '../_helper';
import { SensorType, SensorTypeMap } from '../types';
import { ViolationTrigger } from './violation-trigger';

// EventLog is defined here (this is the authoritative definition).
// The old event-log-data.tsx re-exports it for backward compatibility.
export type EventLog = {
  sensorGroup?: string;
  sensorName?: string;
  sensorType?: SensorType;
  eventType?: string;
  violationTrigger?: string;
  eventSeverity?: string;
  startTime?: string | null;
  endTime?: string | null;
  duration?: string;
  // Millisecond timestamps used by the chart highlight and zoom filter.
  startTimeMs?: number | null;
  endTimeMs?: number | null;
};

type EventLogsProps = {
  sensorTypeMap?: SensorTypeMap;
  queryEventCounts: ReturnType<typeof useGetEventCounts>;
  queryStoreEvents: ReturnType<typeof useGetStoreEvents>;
  onSelectEvent: (eventLog?: EventLog) => void;
  // When set (web only), filters the event log to events that overlap the
  // current zoom window. An event overlaps if start ≤ zoomEnd && end ≥ zoomStart.
  zoomDomain?: { x: [number, number] } | null;
};

export const EventLogs = ({ sensorTypeMap, queryEventCounts, queryStoreEvents, onSelectEvent, zoomDomain }: EventLogsProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const isWeb = Platform.OS === 'web';
  const eventTypes = useEquipmentConfig(ctx => ctx.eventTypes);
  const [filter, setFilter] = useState<string>('all');

  const [tableColumns] = useState<VirtualColumnProps<EventLog>[]>([
    {
      dataField: 'eventType',
      name: t('store_equipment:event_type').toUpperCase(),
      renderCell: ({ item }) => (
        <HStack space={1.5} alignItems="center">
          {item.sensorType === 'temperature' && <Thermometer02Icon size="16px" color="gray.600" />}
          {item.sensorType === 'door' && <DoorEventIcon size="16px" color="gray.600" />}
          <Text size="md" color="black" fontWeight={400} lineHeight="xs">
            {item.eventType}
          </Text>
        </HStack>
      ),
    },
    { dataField: 'violationTrigger', name: t('store_equipment:violation_trigger').toUpperCase() },
    {
      dataField: 'eventSeverity',
      name: t('store_equipment:event_severity').toUpperCase(),
      renderCell: ({ item }) => {
        if (!item.eventSeverity) {
          return item.eventType ? startCase(toLower(item.eventType)) : '';
        }
        return (
          <HStack>
            <ViolationTrigger value={item.eventSeverity} />
          </HStack>
        );
      },
    },
    { dataField: 'startTime', name: t('store_equipment:start_time').toUpperCase() },
    { dataField: 'endTime', name: t('store_equipment:end_time').toUpperCase() },
    { dataField: 'duration', name: t('store_equipment:duration').toUpperCase() },
  ]);

  const events: {
    data: EventLog[];
    offlineEvents: number;
    batteryEvents: number;
    doorEvents: number;
    temperatureEvents: number;
  } = useMemo(() => {
    // Result: { [200]: EventTypeDto, [201]: EventTypeDto }
    const eventTypesLookup = eventTypes?.reduce((_events: Record<number, EventTypeDto>, _event) => {
      if (!_event.EventType) return _events;
      return { ..._events, [_event.EventType]: _event };
    }, {});

    const allEvents: EventLog[] = [
      { eventType: 'temperature', events: flatMap(queryStoreEvents.data?.Equipment, _ => _.TemperatureEvents) },
      { eventType: 'offline', events: flatMap(queryStoreEvents.data?.Equipment, _ => _.RealTimeStatusEvents) },
      { eventType: 'door open', events: flatMap(queryStoreEvents.data?.Equipment, _ => _.DoorEvents) },
      { eventType: 'low battery', events: flatMap(queryStoreEvents.data?.Equipment, _ => _.BatteryEvents) },
    ].reduce((_events: EventLog[], _group) => {
      const __events = map(_group.events, _event => {
        const startTime = stringToDateLocal(_event?.StartTime);
        const endTime = stringToDateLocal(_event?.EndTime);
        const triggerValue = _event?.ViolationTriggerValue;

        let minutes = 0;

        const startTimeStr = formatToShortDateTime(startTime);
        const endTimeStr = formatToShortDateTime(endTime);

        if (startTime && endTime) {
          minutes = dateDiffToMinutes(startTime!, endTime!);
        }

        const violationTrigger = getViolationTriggerLabel(_group.eventType, triggerValue, _event?.ThresholdMode === EventThresholdMode.Upper) || undefined;

        let sensorType: SensorType | undefined;
        if (_group.eventType === 'temperature') {
          sensorType = 'temperature';
        } else if (_group.eventType === 'door open') {
          sensorType = 'door';
        } else if (sensorTypeMap && _event?.InstanceId) {
          sensorType = sensorTypeMap[_event.InstanceId];
        }

        const _eventLog: EventLog = {
          sensorGroup: '',
          sensorName: '',
          sensorType,
          eventType: startCase(_group.eventType),
          violationTrigger: violationTrigger,
          eventSeverity: _event!.Priority,
          duration: convertMinutesToHoursAndMinutes(minutes),
          startTime: startTimeStr,
          endTime: endTimeStr,
          startTimeMs: startTime ? startTime.getTime() : null,
          endTimeMs: endTime ? endTime.getTime() : null,
        };

        return _eventLog;
      });

      return [..._events, ...__events];
    }, []);

    // Stack filters: first by type pill, then (if zoomed on web) by overlap
    // with the zoom window. An event "overlaps" if its [start, end] range
    // intersects the zoom [start, end]. Open-ended events (endTimeMs null)
    // are treated as still ongoing.
    const typeFilteredEvents =
      filter === 'all' ? allEvents : allEvents.filter(event => event?.eventType?.toLocaleLowerCase().includes(filter));

    const zoomFilteredEvents =
      zoomDomain && isWeb
        ? typeFilteredEvents.filter(event => {
            if (event.startTimeMs == null) return false;
            const [zoomStart, zoomEnd] = zoomDomain.x;
            const eventEnd = event.endTimeMs ?? Infinity;
            return event.startTimeMs <= zoomEnd && eventEnd >= zoomStart;
          })
        : typeFilteredEvents;

    return {
      data: orderBy(zoomFilteredEvents, ['startTime'], ['desc']),
      offlineEvents: allEvents.filter(e => e.eventType === 'Offline').length,
      batteryEvents: allEvents.filter(e => e.eventType === 'Low Battery').length,
      doorEvents: allEvents.filter(e => e.eventType === 'Door Open').length,
      temperatureEvents: allEvents.filter(e => e.eventType === 'Temperature').length,
    };
  }, [eventTypes, queryStoreEvents.data, sensorTypeMap, filter, zoomDomain]);

  return (
    <VStack flex={1} space={2} py={2}>
      {/* Event Log - Filter UI */}
      <HStack w="full" alignItems="center" justifyContent="space-between">
        <Text size="xl" color="black" fontWeight={700} lineHeight="xs">
          {t('store_equipment:event_log')}
        </Text>

        {/* Filter View UI */}
        <HStack alignItems="center" space={2} justifyContent="flex-end">
          <ButtonGroup value={filter} onChange={setFilter}>
            <ButtonGroup.Item value="all" minWidth="44px">
              {t('store_equipment:all')}
            </ButtonGroup.Item>
            <ButtonGroup.Item value="temperature" pl={1} startIcon={<Thermometer02Icon color="black" size="18px" />}>
              {events.temperatureEvents.toString()}
            </ButtonGroup.Item>
            <ButtonGroup.Item value="door" pl={1} startIcon={<DoorEventIcon color="black" size="18px" />}>
              {events.doorEvents.toString()}
            </ButtonGroup.Item>
            <ButtonGroup.Item value="offline" pl={1} startIcon={<WifiOffIcon color="black" size="18px" />}>
              {events.offlineEvents.toString()}
            </ButtonGroup.Item>
            <ButtonGroup.Item value="battery" pl={1} startIcon={<BatteryLowIcon color="black" size="18px" />}>
              {events.batteryEvents.toString()}
            </ButtonGroup.Item>
          </ButtonGroup>
        </HStack>
      </HStack>

      {/* Event Logs Table */}
      <View w="full" flex={1}>
        <Box maxHeight="full">
          <VirtualTable
            bordered
            columns={tableColumns}
            data={events.data}
            onItemPress={onSelectEvent}
            renderEmptyData={() => {
              const isLoading = queryStoreEvents.isIdle || queryStoreEvents.isLoading || queryEventCounts.isLoading;
              if (isLoading) return <ResourceLoader isEmpty isLoading minHeight={100} />;
              return (
                <VStack alignItems="center" justifyContent="center" minH={100} space={2} py={4}>
                  <FaceSmileIcon color="#2970FF" size={8} />
                  <VStack alignItems="center" space={1}>
                    <Text size="md" color="gray.700" fontWeight={600} lineHeight="lg">
                      {t('store_equipment:no_events_title')}
                    </Text>
                    <Text size="sm" color="gray.500" fontWeight={400} lineHeight="md">
                      {t('store_equipment:no_events_subtitle')}
                    </Text>
                  </VStack>
                </VStack>
              );
            }}
          />
        </Box>
      </View>
    </VStack>
  );
};
